import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AccountNav } from "~/components/account/account-nav";
import { PageHeader } from "~/components/shell/app-shell";
import { Card } from "~/components/ui/card";
import { Switch } from "~/components/ui/choice";
import { Label } from "~/components/ui/field";
import { toast } from "~/components/ui/toaster";
import { api, unwrap } from "~/lib/api";
import { errorMessage, meQuery, useCurrentUser, type Me } from "~/lib/auth";

export const Route = createFileRoute("/_app/account/notifications")({
  component: NotificationsPage,
});

function NotificationsPage() {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  const update = useMutation({
    mutationFn: (notifyNewLeads: boolean) =>
      unwrap(api.api.me.$patch({ json: { notifyNewLeads } })),
    onMutate: async (notifyNewLeads) => {
      await queryClient.cancelQueries({ queryKey: meQuery.queryKey });
      const previous = queryClient.getQueryData<Me | null>(meQuery.queryKey);
      queryClient.setQueryData(meQuery.queryKey, (old) =>
        old ? { ...old, user: { ...old.user, notifyNewLeads } } : old,
      );
      return { previous };
    },
    onError: (error, _value, context) => {
      queryClient.setQueryData(meQuery.queryKey, context?.previous);
      toast.error(errorMessage(error));
    },
    onSuccess: (_data, value) =>
      toast.success(value ? "New-lead emails turned on." : "New-lead emails turned off."),
    onSettled: () => queryClient.invalidateQueries({ queryKey: meQuery.queryKey }),
  });

  return (
    <>
      <PageHeader title="Account" description="Choose which emails Purlink sends you." />
      <AccountNav />
      <Card className="max-w-3xl">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="notify-new-leads" className="text-h3 text-fg">
              New lead alerts
            </Label>
            <p id="notify-new-leads-description" className="text-body text-fg-secondary">
              Get an email whenever someone submits the enquiry form. You'll still see new leads in
              Purlink either way.
            </p>
          </div>
          <Switch
            id="notify-new-leads"
            aria-describedby="notify-new-leads-description"
            checked={user.notifyNewLeads}
            onCheckedChange={(checked) => update.mutate(checked)}
          />
        </div>
        <p className="mt-5 border-t border-border pt-5 text-small text-fg-muted">
          Security emails (password changes, sign-in codes, account changes) are always sent.
        </p>
      </Card>
    </>
  );
}
